import { Origin } from '@/types/ProductionValueCheck';

import { Packages, ProgramType } from 'types';

// Solar-family programs whose install CTA runs the production-value-check / itc-system-design
// waterfall (those pages are solar-only). Every other program routes directly to
// installation-package/create. Modeling the waterfall as the allowlist keeps the default safe:
// a new program that doesn't opt in never gets misrouted to solar-only pages.
const SOLAR_WATERFALL_PROGRAMS = new Set<ProgramType>([
  ProgramType.solar,
  ProgramType.consumerSolar,
  ProgramType.newHomes,
  ProgramType.newCommunity,
]);

export interface MilestonePackageRouteContext {
  accountId: string;
  programType: ProgramType;
  packageType: Packages;
  isSolarPR?: boolean;
  hasActiveQuote?: boolean;
  hasAttestment?: boolean;
  wasSubmittedBefore2026?: boolean;
  hasDesignDocsQueue?: boolean;
  hasPtoQueue?: boolean;
  hasPtoFlagsEnabled?: boolean;
}

export interface MilestonePackageRouteResult {
  path: string;
  state?: { from: Origin };
}

export const resolveMilestonePackageRoute = (ctx: MilestonePackageRouteContext): MilestonePackageRouteResult => {
  const base = `/accounts/${ctx.accountId}`;

  switch (ctx.packageType) {
    case Packages.loopFieldPackage:
      return {
        path: ctx.hasDesignDocsQueue
          ? `${base}/loop-field-package/create`
          : `${base}/loop-field-design-documents/create`,
      };

    case Packages.installPackage:
      if (!SOLAR_WATERFALL_PROGRAMS.has(ctx.programType)) {
        return { path: `${base}/installation-package/create` };
      }
      if (ctx.hasActiveQuote && !ctx.hasAttestment) {
        return { path: `${base}/production-value-check`, state: { from: Origin.ACCOUNT_VIEW } };
      }
      if (!ctx.wasSubmittedBefore2026) {
        return { path: `${base}/itc-system-design` };
      }
      return { path: `${base}/installation-package/create` };

    case Packages.activationPackage: {
      const skipPto = ctx.programType === ProgramType.doePr || ctx.isSolarPR;
      if (!ctx.hasPtoQueue && ctx.hasPtoFlagsEnabled && !skipPto) {
        return { path: `${base}/proof-of-pto/edit` };
      }
      return { path: `${base}/activation-package/create` };
    }

    case Packages.postActivationPackage:
      return { path: `${base}/post-activation-package/create` };

    default:
      return { path: `${base}/installation-package/create` };
  }
};
